/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { mixins } from '@blend/consumer-components';
import { CommonAPI } from '../../../../../api/common/responses';
import { ReviewUserModal } from './ReviewUserModal';
import { VerifyUserModal } from './VerifyUserModal';
import { ActionButton } from '@components/ActionButton/ActionButton';
import { Tag } from '@components/Tag/Tag';
import { getConfig } from '@utils/configs';
import { formatFullName } from '@utils/formatting';

interface User {
  isNotary: boolean;
  user: CommonAPI.User;
  usersStatus: string;
}

const styles = StyleSheet.create({
  wrapper: {
    ...mixins.marginVertical(8),
  },
  nameWrapper: {
    fontSize: 16,
  },
  tagWrapper: mixins.margin(6, 0),
  pendingVerification: {
    color: '#ED0010',
    marginBottom: 10,
    fontWeight: 450,
    fontSize: 10,
  },
  pendingVerificationText: {
    backgroundColor: '#FBF0F8',
    padding: '2px 4px 2px 4px',
    borderRadius: 4,
  },
});

export const User: React.FC<User> = ({ isNotary, user, usersStatus }) => {
  const {
    firstName,
    middleName,
    lastName,
    suffix,
    indicatorText,
    indicatorType,
    showReviewButton,
    title,
    showApprovalButton,
  } = user;
  const [modalOpen, setModalOpen] = useState(false);

  const isManualVerificaionByNotaryEnabled = getConfig().isManualVerificaionByNotaryEnabled;
  const hasUserBeenVerifiedOrIsOnboarding = !showApprovalButton || usersStatus === 'onboarding';
  const isUserPendingVerification =
    showApprovalButton && usersStatus === 'inVideoCall' && isManualVerificaionByNotaryEnabled;

  let modal;
  if (isManualVerificaionByNotaryEnabled) {
    modal = hasUserBeenVerifiedOrIsOnboarding ? (
      <ReviewUserModal modalOpen={modalOpen} setModalOpen={setModalOpen} user={user} data-it={'review-user-modal'} />
    ) : (
      <VerifyUserModal modalOpen={modalOpen} setModalOpen={setModalOpen} user={user} data-it={'verify-user-modal'} />
    );
  } else {
    modal = (
      <ReviewUserModal modalOpen={modalOpen} setModalOpen={setModalOpen} user={user} data-it={'review-user-modal'} />
    );
  }

  return (
    <div className={css(styles.wrapper)} data-it="User" key={`${firstName} ${lastName}`}>
      <div>
        <div className={css(styles.nameWrapper)}>{formatFullName({ firstName, middleName, lastName, suffix })}</div>
        {isNotary && title !== 'Notary' && isUserPendingVerification && isManualVerificaionByNotaryEnabled && (
          <div data-it="user-pending-verification" className={css(styles.pendingVerification)}>
            <span className={css(styles.pendingVerificationText)}>PENDING MANUAL VERIFICATION</span>
          </div>
        )}
      </div>
      {isNotary && showReviewButton && (
        <ActionButton
          data-dd-privacy="allow"
          sizing="xs"
          option="secondary"
          onClick={() => setModalOpen(true)}
          data-it={`ReviewUserModalButton-${user.participantSessionId}`}
        >
          {hasUserBeenVerifiedOrIsOnboarding || !isManualVerificaionByNotaryEnabled ? <>View ID</> : <>Verify ID</>}
        </ActionButton>
      )}
      {modalOpen && modal}
      {indicatorText && (
        <span className={css(styles.tagWrapper)} data-it="Tag">
          <Tag type={indicatorType}>{indicatorText}</Tag>
        </span>
      )}
    </div>
  );
};
